import React, { useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { useQuery, useMutation } from "@tanstack/react-query";
import { toast } from "sonner";
import { MapPin, Users, BedDouble, Bath, Clock, Check, Waves } from "lucide-react";
import { Layout } from "../components/Layout";
import { eur } from "../lib/format";
import * as api from "../api";

const AMEN_LABEL = {
  wifi: "Wi-Fi", aria_condizionata: "Aria condizionata", piscina: "Piscina", cucina: "Cucina",
  colazione: "Colazione", vista_mare: "Vista mare", parcheggio: "Parcheggio", animali_ammessi: "Animali ammessi",
  accessibilita: "Accessibilità", riscaldamento: "Riscaldamento", lavatrice: "Lavatrice", tv: "TV",
  balcone: "Balcone", giardino: "Giardino", bbq: "BBQ",
};

export default function AccommodationDetail() {
  const { slug } = useParams();
  const [sp] = useSearchParams();
  const [search, setSearch] = useState({
    checkin: sp.get("checkin") || "", checkout: sp.get("checkout") || "", guests: sp.get("guests") || "2",
  });
  const params = { checkin: search.checkin || undefined, checkout: search.checkout || undefined, guests: search.guests || undefined };
  const { data: acc, isLoading, refetch } = useQuery({
    queryKey: ["accDetail", slug, JSON.stringify(params)],
    queryFn: () => api.getAccommodation(slug, params),
  });
  const [selectedUnit, setSelectedUnit] = useState(null);
  const [customer, setCustomer] = useState({ name: "", surname: "", email: "", phone: "" });
  const [checkinSlot, setCheckinSlot] = useState("");

  const book = useMutation({
    mutationFn: (payload) => api.bookAccommodation(payload),
    onSuccess: (r) => { toast.success(`Prenotazione confermata: ${r.booking_number}`); setSelectedUnit(null); refetch(); },
    onError: (e) => toast.error(e.response?.data?.detail || "Prenotazione non riuscita"),
  });

  if (isLoading) return <Layout><div className="text-center py-24 text-slate-400">Caricamento...</div></Layout>;
  if (!acc) return <Layout><div className="text-center py-24 text-slate-400">Struttura non trovata.</div></Layout>;

  const hasDates = acc.search?.nights;
  const gallery = acc.images?.length ? acc.images : (acc.cover ? [acc.cover] : []);

  const doBook = (unit) => {
    if (!search.checkin || !search.checkout) return toast.error("Seleziona le date di soggiorno");
    if (!customer.name || !customer.email) return toast.error("Inserisci nome ed email");
    book.mutate({
      accommodation_id: acc.id, unit_id: unit.id,
      check_in: search.checkin, check_out: search.checkout,
      adults: parseInt(search.guests) || 1, children: 0, units: 1,
      customer: { ...customer, checkin_slot: checkinSlot || undefined },
    });
  };

  return (
    <Layout noPadding>
      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-8">
        {/* gallery */}
        <div className="grid grid-cols-4 gap-2 rounded-2xl overflow-hidden h-[280px] sm:h-[360px] bg-slate-100" data-testid="acc-detail-gallery">
          {gallery.slice(0, 1).map((img, i) => <img key={i} src={img} alt="" className="col-span-4 sm:col-span-2 row-span-2 h-full w-full object-cover" />)}
          {gallery.slice(1, 5).map((img, i) => <img key={i} src={img} alt="" className="hidden sm:block h-full w-full object-cover" />)}
        </div>

        <div className="mt-6 grid lg:grid-cols-3 gap-8">
          <div className="lg:col-span-2 space-y-6">
            <div>
              <div className="text-xs font-bold uppercase tracking-wide text-slate-400">{acc.type?.replace("_", " ")}</div>
              <h1 className="font-heading font-semibold text-3xl sm:text-4xl text-slate-900 mt-1">{acc.name}</h1>
              <p className="mt-2 text-slate-500 flex items-center gap-1"><MapPin className="h-4 w-4" /> {acc.address ? acc.address + ", " : ""}{acc.city} {acc.province && `(${acc.province})`}</p>
            </div>
            {acc.description && <p className="text-slate-600 leading-relaxed whitespace-pre-line">{acc.description}</p>}

            <div className="flex flex-wrap gap-4 text-sm text-slate-600 border-y border-slate-100 py-4">
              <span className="flex items-center gap-1.5"><Users className="h-4 w-4 text-primary" /> Fino a {acc.max_guests} ospiti</span>
              <span className="flex items-center gap-1.5"><Clock className="h-4 w-4 text-primary" /> Check-in {acc.checkin_time} · Check-out {acc.checkout_time}</span>
              {acc.cin && <span className="text-xs text-slate-400">CIN {acc.cin}</span>}
            </div>

            {acc.amenities?.length > 0 && (
              <div>
                <h3 className="font-heading font-semibold text-slate-900 mb-3">Servizi</h3>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                  {acc.amenities.map((a) => <span key={a} className="flex items-center gap-2 text-sm text-slate-600"><Check className="h-4 w-4 text-emerald-500" /> {AMEN_LABEL[a] || a}</span>)}
                </div>
              </div>
            )}
            {acc.rules && <div><h3 className="font-heading font-semibold text-slate-900 mb-2">Regole struttura</h3><p className="text-sm text-slate-600 whitespace-pre-line">{acc.rules}</p></div>}

            {/* units */}
            <div>
              <h3 className="font-heading font-semibold text-slate-900 mb-3">Camere & unità</h3>
              <div className="space-y-3">
                {(acc.units || []).filter((u) => u.active).map((u) => (
                  <div key={u.id} className="rounded-xl border border-slate-200 p-4 flex flex-col sm:flex-row gap-4" data-testid={`acc-unit-${u.id}`}>
                    {u.images?.[0] && <img src={u.images[0]} alt="" className="h-24 w-full sm:w-32 object-cover rounded-lg" />}
                    <div className="flex-1">
                      <div className="font-semibold text-slate-800">{u.name}</div>
                      <div className="text-xs text-slate-400 flex flex-wrap gap-3 mt-1">
                        <span className="flex items-center gap-1"><Users className="h-3 w-3" /> {u.capacity_total} ospiti</span>
                        <span className="flex items-center gap-1"><BedDouble className="h-3 w-3" /> {u.beds} letti</span>
                        <span className="flex items-center gap-1"><Bath className="h-3 w-3" /> {u.bathrooms} bagni</span>
                        {u.size_sqm && <span>{u.size_sqm} m²</span>}
                        <span>min {u.min_stay} notti</span>
                      </div>
                      {u.description && <p className="text-xs text-slate-500 mt-1">{u.description}</p>}
                    </div>
                    <div className="text-right sm:min-w-[140px]">
                      {hasDates ? (
                        <>
                          <div className="text-xs text-slate-400">{acc.search.nights} notti</div>
                          <div className="font-bold text-primary text-lg">{eur(u.stay_total)}</div>
                          <div className="text-[11px] text-slate-400 mb-2">{u.available > 0 ? `${u.available} disponibili` : "Non disponibile"}</div>
                          <button data-testid={`acc-book-unit-${u.id}`} disabled={!u.bookable}
                            onClick={() => setSelectedUnit(u)}
                            className={`rounded-full text-sm font-semibold px-5 py-2 ${u.bookable ? "bg-primary text-white hover:bg-primary/90" : "bg-slate-100 text-slate-400 cursor-not-allowed"}`}>
                            {u.bookable ? "Prenota" : (u.available === 0 ? "Esaurito" : `min ${u.min_stay_required} notti`)}
                          </button>
                        </>
                      ) : (
                        <>
                          <div className="text-xs text-slate-400">da</div>
                          <div className="font-bold text-primary text-lg">{eur(u.from_price)}</div>
                          <div className="text-[11px] text-slate-400">/ notte</div>
                        </>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* booking sidebar */}
          <aside className="lg:sticky lg:top-24 h-fit">
            <div className="rounded-2xl border border-slate-200 shadow-sm p-5 space-y-3" data-testid="acc-booking-widget">
              <div className="font-heading font-semibold text-slate-900">Verifica disponibilità</div>
              <label className="block"><span className="text-[10px] font-bold uppercase text-slate-400">Check-in</span>
                <input data-testid="acc-detail-checkin" type="date" value={search.checkin} onChange={(e) => setSearch({ ...search, checkin: e.target.value })} className="mt-1 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm" /></label>
              <label className="block"><span className="text-[10px] font-bold uppercase text-slate-400">Check-out</span>
                <input data-testid="acc-detail-checkout" type="date" value={search.checkout} onChange={(e) => setSearch({ ...search, checkout: e.target.value })} className="mt-1 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm" /></label>
              <label className="block"><span className="text-[10px] font-bold uppercase text-slate-400">Ospiti</span>
                <input data-testid="acc-detail-guests" type="number" min="1" value={search.guests} onChange={(e) => setSearch({ ...search, guests: e.target.value })} className="mt-1 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm" /></label>
              <button data-testid="acc-detail-check-btn" onClick={() => refetch()} className="w-full rounded-full bg-slate-900 text-white font-semibold py-2.5 text-sm">Aggiorna disponibilità</button>
              {hasDates && <p className="text-xs text-slate-400 text-center">Seleziona una camera qui accanto per prenotare.</p>}
            </div>
          </aside>
        </div>
      </div>

      {/* booking modal */}
      {selectedUnit && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={() => setSelectedUnit(null)}>
          <div className="bg-white rounded-2xl max-w-md w-full p-6 space-y-4" onClick={(e) => e.stopPropagation()} data-testid="acc-book-modal">
            <h3 className="font-heading font-semibold text-lg text-slate-900">Prenota {selectedUnit.name}</h3>
            <p className="text-sm text-slate-500">{search.checkin} → {search.checkout} · {acc.search.nights} notti · {search.guests} ospiti</p>
            <div className="rounded-lg bg-slate-50 p-3 flex justify-between"><span className="text-sm text-slate-500">Totale soggiorno</span><span className="font-bold text-primary">{eur(selectedUnit.stay_total)}</span></div>
            {acc.checkin_time_selection_enabled && acc.checkin_slots?.length > 0 && (
              <label className="block"><span className="text-[10px] font-bold uppercase text-slate-400">Fascia di check-in</span>
                <select value={checkinSlot} onChange={(e) => setCheckinSlot(e.target.value)} className="mt-1 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm">
                  <option value="">Seleziona...</option>
                  {acc.checkin_slots.map((s) => <option key={s} value={s}>{s}</option>)}
                </select></label>
            )}
            <div className="grid grid-cols-2 gap-2">
              <input data-testid="acc-cust-name" placeholder="Nome" value={customer.name} onChange={(e) => setCustomer({ ...customer, name: e.target.value })} className="rounded-lg border border-slate-200 px-3 py-2 text-sm" />
              <input data-testid="acc-cust-surname" placeholder="Cognome" value={customer.surname} onChange={(e) => setCustomer({ ...customer, surname: e.target.value })} className="rounded-lg border border-slate-200 px-3 py-2 text-sm" />
            </div>
            <input data-testid="acc-cust-email" placeholder="Email" value={customer.email} onChange={(e) => setCustomer({ ...customer, email: e.target.value })} className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm" />
            <input data-testid="acc-cust-phone" placeholder="Telefono" value={customer.phone} onChange={(e) => setCustomer({ ...customer, phone: e.target.value })} className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm" />
            <button data-testid="acc-confirm-booking" onClick={() => doBook(selectedUnit)} disabled={book.isPending} className="w-full rounded-full bg-primary text-white font-semibold py-3">{book.isPending ? "Prenotazione..." : "Conferma prenotazione"}</button>
            <p className="text-[11px] text-slate-400 text-center">Nessun pagamento richiesto in questa fase.</p>
          </div>
        </div>
      )}
    </Layout>
  );
}
